import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import LandingsManagerBase from "./LandingsManagerBase";

// Temas de eventos destino: las cards deslizantes de la home ("Boda frente
// al mar", "Corporativo en un espacio inmersivo"...). Cada tema referencia
// un tipo de evento + una ubicación (el link va a esa landing combo) y el
// venue del que sale la foto. El homesite descarta solo los temas cuyo
// evento/ubicación esté inactivo o cuyo venue deje de publicarse — no puede
// quedar una card rota. Mientras la colección esté VACÍA, la home muestra
// una lista curada en código; el primer tema guardado toma el control.

// Zona del venue: basicInfo dict {"6": zona} o legacy list {name: "Zona"}.
function zoneOf(venue) {
  const bi = venue.basicInfo;
  if (bi && !Array.isArray(bi) && typeof bi === "object") return bi["6"] || "";
  if (Array.isArray(bi)) {
    const f = bi.find((i) => i && i.name === "Zona");
    return f?.value || "";
  }
  return "";
}

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function TemasDestinoManager() {
  const [catalogs, setCatalogs] = useState(null); // {eventTypes, ciudades, venues}

  useEffect(() => {
    (async () => {
      const [ets, cds, vns, tiposSnap] = await Promise.all([
        getDocs(collection(db, "eventTypes")),
        getDocs(collection(db, "ciudades")),
        getDocs(collection(db, "venues")),
        getDoc(doc(db, "openData", "tiposVenues")),
      ]);
      setCatalogs({
        eventTypes: ets.docs.map((d) => ({ id: d.id, ...d.data() })),
        ciudades: cds.docs.map((d) => ({ id: d.id, ...d.data() })),
        venues: vns.docs.map((d) => ({ id: d.id, ...d.data() })),
        tiposVenue: tiposSnap.exists() ? tiposSnap.data().tipos || [] : [],
      });
    })();
  }, []);

  const config = useMemo(() => {
    if (!catalogs) return null;
    const { eventTypes, ciudades, venues, tiposVenue } = catalogs;

    const eventById = new Map(eventTypes.map((e) => [e.id, e]));
    const ciudadById = new Map(ciudades.map((c) => [c.id, c]));
    const venueById = new Map(venues.map((v) => [v.id, v]));

    const publicado = (v) => v.isActive && v.isActiveAdmin;

    // Los campos de guía (evento/ubicación/filtro/mapa) no aplican a un tema
    // "espacio" (su link va directo a la ficha del venue) — se ocultan.
    const soloGuia = (draft) =>
      (draft.tipo || (draft.ciudadId ? "destino" : "espacio")) !== "espacio";

    return {
      collection: "temasDestino",
      title: "Temas destino (home)",
      singular: "tema",
      subtitle:
        "Cards deslizantes de la página principal, en DOS filas según su tipo: \"Guía de destino\" (eventos destino — si la ciudad tiene guía editorial, la card lleva el sello Guía y manda a /eventos-destino/{ciudad}) y \"Espacio con carácter\" (venues singulares — cavas, ex conventos, foros). Cada tema usa la foto principal del venue seleccionado. El orden aquí no importa en el sitio (se barajan al azar en cada visita).",
      hasHomeDesc: false,
      hasLandingCopy: false,
      identityFields: [
        {
          path: "titulo.ES",
          label: "Título (ES)",
          placeholder: "Boda frente al mar",
          hint: "El texto grande de la card.",
        },
        {
          path: "titulo.EN",
          label: "Título (EN)",
          placeholder: "Wedding on the beach",
          hint: "Si falta, se muestra el español.",
        },
        {
          path: "subtitulo.ES",
          label: "Subtítulo (ES)",
          placeholder: "Brisa, arena y atardecer en Acapulco",
          hint: "Línea corta debajo del título.",
        },
        {
          path: "subtitulo.EN",
          label: "Subtítulo (EN)",
          placeholder: "Breeze, sand and sunsets in Acapulco",
        },
        {
          path: "tipo",
          label: "Tipo de card (fila de la home)",
          type: "select",
          options: [
            {
              value: "destino",
              label: "Guía de destino — fila \"Eventos destino en México\"",
            },
            {
              value: "espacio",
              label: "Espacio con carácter — fila \"Espacios con carácter\"",
            },
          ],
          hint: "Guía de destino = el evento vale por el lugar al que se viaja; el link va a la landing (o a la guía editorial si la ciudad tiene una). Espacio con carácter = el venue en sí es el gancho (el sótano, lo inmersivo, lo industrial); la card muestra sus clasificaciones como badges y el link va DIRECTO a la ficha del venue — tipo de evento y ubicación no aplican. Los temas viejos sin este campo se acomodan solos: con ubicación → guía, sin ubicación → espacio.",
        },
        {
          path: "eventTypeId",
          label: "Tipo de evento (destino del link)",
          type: "select",
          visibleIf: soloGuia,
          options: eventTypes.map((e) => ({
            value: e.id,
            label: `${e.label?.ES || e.id}${e.activo ? "" : " (inactivo — la card no se muestra)"}`,
          })),
          hint: "Con ubicación, el link va a /{evento}/{ubicación}; sin ubicación, a /{evento}.",
        },
        {
          path: "ciudadId",
          label: "Ubicación (opcional)",
          type: "select",
          visibleIf: soloGuia,
          emptyLabel: "— Sin ubicación (link solo al tipo de evento) —",
          options: ciudades.map((c) => ({
            value: c.id,
            label: `${c.titulo?.ES || c.name || c.id}${c.activo ? "" : " (inactiva — la card no se muestra)"}`,
          })),
          hint: "Déjalo vacío para que la card mande a la landing del tipo de evento sin filtrar por ciudad. IMPORTANTE: las guías CON ubicación también alimentan la sección \"Ideas para tu evento en {ciudad}\" de la guía editorial de esa ciudad (/eventos-destino/{ciudad}) — para agregar una idea a una guía, crea un tema tipo Guía con esa ubicación.",
        },
        {
          path: "venueType",
          label: "Tipo de venue (filtro de la búsqueda)",
          type: "select",
          visibleIf: soloGuia,
          emptyLabel: "— Sin filtro de tipo —",
          options: tiposVenue.map((t) => ({ value: t, label: t })),
          hint: "Opcional. La landing destino abre con este tipo ya filtrado (p. ej. solo Haciendas).",
        },
        {
          path: "abrirMapa",
          label: "Abrir con mapa",
          type: "checkbox",
          visibleIf: soloGuia,
          checkboxLabel: "La landing destino abre con el mapa visible",
          hint: "Opcional. Agrega map=1 al link de la card.",
        },
        {
          path: "venueId",
          label: "Venue de la foto",
          type: "select",
          options: venues
            .slice()
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map((v) => ({
              value: v.id,
              label: `${v.name || v.id} — ${zoneOf(v) || "sin zona"}${publicado(v) ? "" : " (no publicado — la card no se muestra)"}`,
            })),
          hint: "La card usa la primera foto de este venue (abajo la ves).",
        },
      ],
      preview: (draft) => {
        const venue = venueById.get(draft.venueId);
        const img = venue?.images?.[0];
        if (!img)
          return (
            <p className="mt-4 text-xs text-gray-500">
              Elige un venue para ver la foto que usaría la card.
            </p>
          );
        return (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-2">
              Foto de la card ({venue.name}):
            </p>
            <img
              src={img}
              alt={venue.name}
              className="h-36 w-64 object-cover rounded-lg border border-gray-200"
            />
          </div>
        );
      },
      rowMedia: (item) => {
        const img = venueById.get(item.venueId)?.images?.[0];
        return img ? (
          <img
            src={img}
            alt=""
            className="h-12 w-16 shrink-0 rounded-lg border border-zinc-200 object-cover"
          />
        ) : (
          <div className="h-12 w-16 shrink-0 rounded-lg bg-zinc-100" />
        );
      },
      rowLabel: (d) => d.titulo?.ES || "(sin título)",
      rowGroups: ["Guías de destino", "Espacios con carácter"],
      rowGroup: (d) =>
        (d.tipo || (d.ciudadId ? "destino" : "espacio")) === "espacio"
          ? "Espacios con carácter"
          : "Guías de destino",
      rowDetail: (d) => {
        const vn = venueById.get(d.venueId);
        const esEspacio =
          (d.tipo || (d.ciudadId ? "destino" : "espacio")) === "espacio";
        if (esEspacio) return `Link a la ficha de ${vn?.name || "?"}`;
        const ev = eventById.get(d.eventTypeId);
        const cd = ciudadById.get(d.ciudadId);
        const ciudad = d.ciudadId
          ? cd?.titulo?.ES || cd?.name || "?"
          : "sin ciudad";
        return `${ev?.label?.ES || "?"} × ${ciudad} · foto: ${vn?.name || "?"}`;
      },
      newDraft: () => ({
        titulo: { ES: "", EN: "" },
        subtitulo: { ES: "", EN: "" },
        tipo: "destino",
        eventTypeId: "",
        ciudadId: "",
        venueType: "",
        abrirMapa: false,
        venueId: "",
        activo: false,
        orden: 999,
      }),
      docIdFor: (draft) => slugify(draft.titulo?.ES),
      validateDraft: (draft) => {
        if (!draft.titulo?.ES?.trim()) return "Falta el título en español";
        const esEspacio =
          (draft.tipo || (draft.ciudadId ? "destino" : "espacio")) === "espacio";
        if (!esEspacio && !draft.eventTypeId)
          return "Falta el tipo de evento (las guías de destino lo necesitan para el link)";
        if (!draft.venueId) return "Falta el venue de la foto";
        if (!venueById.get(draft.venueId)?.images?.[0])
          return "El venue elegido no tiene fotos — elige otro";
        return null;
      },
      deleteDescription: (item) =>
        `¿Eliminar "${item.titulo?.ES || "(sin título)"}"? La card desaparecerá de la página principal. Si solo quieres ocultarla temporalmente, usa el toggle Activa/Inactiva. Ojo: si eliminas TODOS los temas, el sitio vuelve a mostrar la lista curada original.`,
    };
  }, [catalogs]);

  if (!config) return <div className="p-4">Cargando...</div>;
  return <LandingsManagerBase config={config} />;
}
